import { Layout } from 'antd';
import { Suspense, lazy, useEffect } from 'react';
import { Route, Routes as RouterRoutes } from 'react-router';

import { useVerifyTokenMutation } from '@/entities/session';
import { useGetUserInfoQuery } from '@/entities/user';
import { NotFoundPage } from '@/pages/not-found';
import { i18n } from '@/shared/config/i18n/i18n';
import { useWindowSize } from '@/shared/lib/useWindowSize';
import { useAppSelector } from '@/shared/model/storeHooks';

import { ExternalPages } from './ExternalPages';
import { GuardedLayout, GuestRoute, PrivateRoute } from './RouteGuards';
import { StartupScreen } from './StartupScreen';
import { LAYOUTS, routes } from './routes';
import type { RouteConfig } from './routes';

import layout from '@/app/styles/AppLayout.module.css';

const InternalPages = lazy(() =>
  import('./InternalPages').then((m) => ({ default: m.InternalPages })),
);

const renderRoute = ({ path, Component, guest, access }: RouteConfig) => {
  let element = <Component />;

  if (guest) {
    element = <GuestRoute key={path}>{element}</GuestRoute>;
  } else if (access) {
    element = <PrivateRoute access={access}>{element}</PrivateRoute>;
  }

  return <Route key={path} path={path} element={element} />;
};

const renderRoutes = (layout?: string) =>
  routes.filter((route) => route.layout === layout).map(renderRoute);

export function AppRouter() {
  const [, innerHeight] = useWindowSize();
  const [verifyToken] = useVerifyTokenMutation();
  const { accessToken, refreshToken, isValidRefreshToken } = useAppSelector(
    (store) => store.auth,
  );
  const { data: userInfo } = useGetUserInfoQuery(undefined, {
    skip: !accessToken,
  });

  useEffect(() => {
    if (innerHeight) {
      document.documentElement.style.setProperty(
        '--app-height',
        `${innerHeight}px`,
      );
    }
  }, [innerHeight]);

  useEffect(() => {
    if (accessToken && refreshToken && !isValidRefreshToken) {
      verifyToken({ token: accessToken });
    }
  }, [accessToken, refreshToken, isValidRefreshToken, verifyToken]);

  useEffect(() => {
    i18n.changeLanguage(userInfo?.language || 'en');
  }, [userInfo?.language]);

  if (refreshToken && !isValidRefreshToken) {
    return (
      <Layout>
        <RouterRoutes>
          <Route path="*" element={<StartupScreen />} />
        </RouterRoutes>
      </Layout>
    );
  }

  return (
    <Layout className={layout.app}>
      <RouterRoutes>
        {renderRoutes(undefined)}

        <Route element={<ExternalPages />}>
          {renderRoutes(LAYOUTS.external)}
        </Route>

        <Route
          element={
            <GuardedLayout
              routes={routes.filter(
                (route) => route.layout === LAYOUTS.internal,
              )}
            >
              <Suspense fallback={null}>
                <InternalPages />
              </Suspense>
            </GuardedLayout>
          }
        >
          {renderRoutes(LAYOUTS.internal)}
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </RouterRoutes>
    </Layout>
  );
}
